// src/pages/AdminPanel.js — v4
// Adds: Weekly Offers tab (fully editable Tue/Thu/Sat style offers)
//       Combos tab (slot-based combo builder)
// Keeps: Menu Items, Add-ons, Offers (coupon-style), Settings

import React, { useEffect, useState, useRef } from 'react';
import {
  collection, onSnapshot, addDoc, updateDoc,
  deleteDoc, doc, serverTimestamp, setDoc, getDocs, writeBatch
} from 'firebase/firestore';
import { db } from '../firebase';
import { MENU_DATA, SIZES } from '../data/menuData';
import toast from 'react-hot-toast';
import { autoSizePrice, fmt } from '../utils/money';
import { optionLabel } from '../utils/addons';
import { DEFAULT_UPSELL_ITEMS } from '../utils/upsell';
import { CUSTOMER_SHEET, CUSTOMER_INSTRUCTIONS, buildCustomerExportRows, SAMPLE_CUSTOMER_ROWS, parseCustomerRows } from '../utils/customerExcel';
import { buildExportRows, parseImportRows, INSTRUCTION_ROWS, SHEET_NAME } from '../utils/menuExcel';

const TABS = ['Menu Items', 'Categories', 'Customers', 'Add-ons', 'Weekly Offers', 'Combos', 'Suggested Items', 'Offers', 'Settings'];
const SIZES_OPTIONS = ['pizza','pasta','burger','fries','single'];
const DAYS = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];
const PIZZA_SIZE_LABELS = ['Personal','Share','Family'];

const DEFAULT_WEEKLY_OFFERS = [
  { id:'wow_tuesday', name:'WOW Tuesday 🔥', dayOfWeek:2,
    description:'Any Family pizza at ₹469! Multiple pizzas allowed. Add-ons extra.',
    type:'fixed_price', sizeFilter:'Family', fixedPrice:469, active:true },
  { id:'wow_thursday', name:'WOW Thursday 🎉', dayOfWeek:4,
    description:'Any Share pizza at ₹369! Multiple pizzas allowed. Add-ons extra.',
    type:'fixed_price', sizeFilter:'Share', fixedPrice:369, active:true },
  { id:'yoyo_saturday', name:'YO YO Saturday 🎊', dayOfWeek:6,
    description:'Buy Share ≥₹439 or Family ≥₹619 → FREE same-size pizza! Add-ons chargeable.',
    type:'buy1get1', rules:[{size:'Share',minPrice:439},{size:'Family',minPrice:619}], active:true },
];

// ⚠️ MUST be defined OUTSIDE the component (module scope) — a stable reference.
// Defining it inside AdminPanel() would recreate it on every render, causing React
// to unmount/remount every wrapped input and lose focus after each keystroke.
function F({ label, children }) {
  return (<div className="field"><label>{label}</label>{children}</div>);
}

export default function AdminPanel() {
  const [tab, setTab]             = useState('Menu Items');
  const [items, setItems]         = useState([]);
  const [addonGroups, setAddonGroups] = useState([]);
  const [offers, setOffers]       = useState([]);
  const [weeklyOffers, setWeeklyOffers] = useState([]);
  const [combos, setCombos]       = useState([]);
  const [settings, setSettings]   = useState({});
  const [settingsForm, setSettingsForm] = useState({});

  const [search, setSearch]       = useState('');
  const [filterCat, setFilterCat] = useState('all');
  const [saving, setSaving]       = useState(false);
  const [seeding, setSeeding]     = useState(false);
  const [seedingOffers, setSeedingOffers] = useState(false);
  const fileInputRef = useRef(null);
  const [importPreview, setImportPreview] = useState(null); // { fileName, updates, changes, errors, stats }
  const [importing, setImporting] = useState(false);

  // categories + suggested items
  // customers (Excel import)
  const [customers, setCustomers]           = useState([]);
  const [customersLoading, setCustomersLoading] = useState(false);
  const [customerPreview, setCustomerPreview]   = useState(null); // { fileName, creates, updates, errors, stats }
  const [custOverwrite, setCustOverwrite]   = useState(false);
  const [custImporting, setCustImporting]   = useState(false);
  const custFileRef = useRef(null);

  const [newCategory, setNewCategory] = useState('');
  const [renaming, setRenaming]       = useState(null);   // { old, value }
  const [deletingCat, setDeletingCat] = useState(null);   // { name, moveTo }
  const [catBusy, setCatBusy]         = useState(false);
  const [upsellDraft, setUpsellDraft] = useState(null);   // list being edited (null = nothing edited)
  const [newUpsell, setNewUpsell]     = useState({ emoji:'🍽️', name:'', price:'' });

  const [itemModal, setItemModal] = useState(null);
  const [itemForm, setItemForm]   = useState({ name:'', cat:'', basePrice:'', emoji:'🍕', sizes:'pizza', hasAddon:true, available:true, sizePrices:{} });

  const [addonModal, setAddonModal] = useState(null);
  const [addonGroupForm, setAddonGroupForm] = useState({ name:'', selectionType:'multiple', appliesTo:['pizza'] });
  const [editingGroup, setEditingGroup] = useState(null);
  const addonNameRef     = React.useRef();
  const addonPersonalRef = React.useRef();
  const addonShareRef    = React.useRef();
  const addonFamilyRef   = React.useRef();

  const [offerModal, setOfferModal] = useState(null);
  const [offerForm, setOfferForm] = useState({ title:'', description:'', type:'percentage', value:'', minOrder:'', code:'', active:true });

  const [weeklyModal, setWeeklyModal] = useState(null); // 'add' | offer object
  const [weeklyForm, setWeeklyForm] = useState({ name:'', dayOfWeek:2, description:'', type:'fixed_price', sizeFilter:'Family', fixedPrice:'', catFilter:[], rules:[{size:'Share',minPrice:''}], active:true });

  const [comboModal, setComboModal] = useState(null); // 'add' | combo object
  const [comboForm, setComboForm] = useState({ name:'', price:'', active:true, slots:[{ id:'slot_'+Date.now(), label:'', itemIds:[], allowedSizes:[] }] });

  useEffect(() => {
    const u1 = onSnapshot(collection(db,'menu'),      s => setItems(s.docs.map(d=>({id:d.id,...d.data()}))));
    const u2 = onSnapshot(collection(db,'addonGroups'),s => setAddonGroups(s.docs.map(d=>({id:d.id,...d.data()})).sort((a,b)=>(a.order||0)-(b.order||0))));
    const u3 = onSnapshot(collection(db,'offers'),    s => setOffers(s.docs.map(d=>({id:d.id,...d.data()}))));
    const u4 = onSnapshot(doc(db,'config','settings'),s => { if(s.exists()){setSettings(s.data());setSettingsForm(s.data());} });
    const u5 = onSnapshot(collection(db,'weeklyOffers'), s => setWeeklyOffers(s.docs.map(d=>({id:d.id,...d.data()}))));
    const u6 = onSnapshot(collection(db,'combos'), s => setCombos(s.docs.map(d=>({id:d.id,...d.data()}))));
    return () => { u1();u2();u3();u4();u5();u6(); };
  },[]);

  // ── MENU ITEMS ─────────────────────────────────────────────
  const seedMenu = async () => {
    if(!window.confirm(`This will add all ${MENU_DATA.length} items from menuData.js to Firestore. Existing items won't be overwritten. Continue?`)) return;
    setSeeding(true);
    // Fetch FRESH from Firestore right now (don't trust possibly-stale `items` state)
    // to get an accurate count and avoid unnecessary rewrites.
    const freshSnap = await getDocs(collection(db,'menu'));
    const existingIds = new Set(freshSnap.docs.map(d => d.id));
    let count = 0;
    for(const item of MENU_DATA) {
      if(!existingIds.has(item.id)) {
        await setDoc(doc(db,'menu',item.id), { ...item, available:true, createdAt:serverTimestamp() });
        count++;
      }
    }
    toast.success(count > 0 ? `Added ${count} new items to Firestore!` : 'All default items already loaded — nothing new to add. Your custom items are safe and untouched.');
    setSeeding(false);
  };

  const saveItem = async () => {
    if(!itemForm.name||!itemForm.basePrice){toast.error('Name and price required');return;}
    setSaving(true);
    try {
      const cleanedSizePrices = {};
      Object.entries(itemForm.sizePrices||{}).forEach(([label,val])=>{ if(val!==''&&val!=null&&!isNaN(val)) cleanedSizePrices[label]=+val; });
      const data = {...itemForm, cat: (itemForm.cat||'').trim(), basePrice:+itemForm.basePrice, sizePrices:cleanedSizePrices, updatedAt:serverTimestamp()};
      if(itemModal==='add') { await addDoc(collection(db,'menu'),{...data,createdAt:serverTimestamp()}); toast.success('Item added!'); }
      else { await updateDoc(doc(db,'menu',itemModal.id),data); toast.success('Item updated!'); }
      setItemModal(null);
    } catch(e){toast.error(e.message);}
    setSaving(false);
  };

  const deleteItem = async(id,name) => { if(!window.confirm(`Delete "${name}"?`))return; await deleteDoc(doc(db,'menu',id)); toast.success('Item deleted'); };
  const toggleAvailable = async(id,current) => { await updateDoc(doc(db,'menu',id),{available:!current}); toast.success(!current?'Item enabled':'Item hidden'); };
  const updatePrice = async(id,price) => { if(!price||isNaN(price))return; await updateDoc(doc(db,'menu',id),{basePrice:+price,updatedAt:serverTimestamp()}); toast.success('Price updated!'); };

  // ── ADDON GROUPS ───────────────────────────────────────────
  const updateOptionPrice = async (groupId, optionId, sizeKey, value) => {
    const group = addonGroups.find(g=>g.id===groupId); if(!group) return;
    const options = (group.options||[]).map(o => o.id===optionId ? {...o, [sizeKey]:+value} : o);
    await updateDoc(doc(db,'addonGroups',groupId),{options, updatedAt:serverTimestamp()});
    toast.success('Price saved!');
  };
  const toggleOption = async(groupId, optionId, current) => {
    const group = addonGroups.find(g=>g.id===groupId); if(!group) return;
    const options = (group.options||[]).map(o => o.id===optionId?{...o,active:!current}:o);
    await updateDoc(doc(db,'addonGroups',groupId),{options});
    toast.success(!current?'Option enabled':'Option hidden');
  };
  const addOptionToGroup = async(groupId) => {
    const name = addonNameRef.current?.value?.trim() || '';
    const personal = +addonPersonalRef.current?.value || 0;
    const share    = +addonShareRef.current?.value    || 0;
    const family   = +addonFamilyRef.current?.value   || 0;
    if(!name){ toast.error('Enter option name'); return; }
    const group = addonGroups.find(g=>g.id===groupId); if(!group) return;
    const newOption = { id: name.toLowerCase().replace(/\s+/g,'_')+'_'+Date.now(), name, personalPrice:personal, sharePrice:share, familyPrice:family, active:true };
    await updateDoc(doc(db,'addonGroups',groupId),{options:[...(group.options||[]), newOption], updatedAt:serverTimestamp()});
    toast.success('Option added!');
    setAddonModal(null);
  };
  const deleteOption = async(groupId, optionId) => {
    if(!window.confirm('Delete this option?'))return;
    const group = addonGroups.find(g=>g.id===groupId); if(!group) return;
    await updateDoc(doc(db,'addonGroups',groupId),{options:(group.options||[]).filter(o=>o.id!==optionId)});
    toast.success('Option deleted');
  };
  const addNewGroup = async() => {
    if(!addonGroupForm.name){toast.error('Enter group name');return;}
    setSaving(true);
    try {
      const id = addonGroupForm.name.toLowerCase().replace(/\s+/g,'_');
      await setDoc(doc(db,'addonGroups',id),{ ...addonGroupForm, id, order: addonGroups.length+1, options:[], createdAt:serverTimestamp() });
      toast.success('Addon group added!');
      setAddonModal(null);
      setAddonGroupForm({name:'',selectionType:'multiple',appliesTo:['pizza']});
    } catch(e){toast.error(e.message);}
    setSaving(false);
  };

  // ── COUPON-STYLE OFFERS ────────────────────────────────────
  const saveOffer = async() => {
    if(!offerForm.title||!offerForm.value){toast.error('Title and value required');return;}
    setSaving(true);
    try {
      const data = {...offerForm,value:+offerForm.value,minOrder:+offerForm.minOrder||0,updatedAt:serverTimestamp()};
      if(offerModal==='add') { await addDoc(collection(db,'offers'),{...data,createdAt:serverTimestamp()}); toast.success('Offer added!'); }
      else { await updateDoc(doc(db,'offers',offerModal.id),data); toast.success('Offer updated!'); }
      setOfferModal(null);
    } catch(e){toast.error(e.message);}
    setSaving(false);
  };
  const toggleOffer = async(id,current) => { await updateDoc(doc(db,'offers',id),{active:!current}); toast.success(!current?'Offer activated':'Offer paused'); };
  const deleteOffer = async(id) => { if(!window.confirm('Delete this offer?'))return; await deleteDoc(doc(db,'offers',id)); toast.success('Offer deleted'); };

  // ── WEEKLY OFFERS ──────────────────────────────────────────
  const seedWeeklyOffers = async () => {
    setSeedingOffers(true);
    let count = 0;
    for (const o of DEFAULT_WEEKLY_OFFERS) {
      const exists = weeklyOffers.find(w => w.id === o.id);
      if (!exists) { await setDoc(doc(db,'weeklyOffers', o.id), { ...o, createdAt: serverTimestamp() }); count++; }
    }
    toast.success(count ? `Added ${count} default offers!` : 'Default offers already exist');
    setSeedingOffers(false);
  };

  const openWeeklyEdit = (offer) => {
    if (offer === 'add') {
      setWeeklyForm({ name:'', dayOfWeek:2, description:'', type:'fixed_price', sizeFilter:'Family', fixedPrice:'', catFilter:[], rules:[{size:'Share',minPrice:''}], active:true });
    } else {
      setWeeklyForm({
        name: offer.name || '', dayOfWeek: offer.dayOfWeek ?? 2, description: offer.description || '',
        type: offer.type || 'fixed_price', sizeFilter: offer.sizeFilter || 'Family', fixedPrice: offer.fixedPrice || '',
        catFilter: offer.catFilter || [], rules: offer.rules?.length ? offer.rules : [{size:'Share',minPrice:''}],
        active: offer.active !== false,
      });
    }
    setWeeklyModal(offer);
  };

  const saveWeeklyOffer = async () => {
    if (!weeklyForm.name) { toast.error('Enter offer name'); return; }
    setSaving(true);
    try {
      const data = {
        name: weeklyForm.name, dayOfWeek: +weeklyForm.dayOfWeek, description: weeklyForm.description,
        type: weeklyForm.type, active: weeklyForm.active, updatedAt: serverTimestamp(),
        ...(weeklyForm.type === 'fixed_price'
          ? { sizeFilter: weeklyForm.sizeFilter, fixedPrice: +weeklyForm.fixedPrice || 0, catFilter: weeklyForm.catFilter.length ? weeklyForm.catFilter : null }
          : { rules: weeklyForm.rules.map(r => ({ size: r.size, minPrice: +r.minPrice || 0 })) }),
      };
      if (weeklyModal === 'add') {
        const id = weeklyForm.name.toLowerCase().replace(/\s+/g,'_') + '_' + Date.now();
        await setDoc(doc(db,'weeklyOffers', id), { ...data, createdAt: serverTimestamp() });
        toast.success('Weekly offer added!');
      } else {
        await updateDoc(doc(db,'weeklyOffers', weeklyModal.id), data);
        toast.success('Weekly offer updated!');
      }
      setWeeklyModal(null);
    } catch(e) { toast.error(e.message); }
    setSaving(false);
  };

  const toggleWeeklyOffer = async (id, current) => { await updateDoc(doc(db,'weeklyOffers',id), { active: !current }); toast.success(!current ? 'Activated' : 'Paused'); };
  const deleteWeeklyOffer = async (id) => { if (!window.confirm('Delete this offer?')) return; await deleteDoc(doc(db,'weeklyOffers',id)); toast.success('Deleted'); };

  const addRuleRow = () => setWeeklyForm(f => ({ ...f, rules: [...f.rules, { size:'Family', minPrice:'' }] }));
  const removeRuleRow = (idx) => setWeeklyForm(f => ({ ...f, rules: f.rules.filter((_,i)=>i!==idx) }));
  const updateRuleRow = (idx, key, val) => setWeeklyForm(f => ({ ...f, rules: f.rules.map((r,i)=>i===idx?{...r,[key]:val}:r) }));

  // ── COMBOS ─────────────────────────────────────────────────
  const openComboEdit = (combo) => {
    if (combo === 'add') {
      setComboForm({ name:'', price:'', active:true, slots:[{ id:'slot_'+Date.now(), label:'', itemIds:[], allowedSizes:[] }] });
    } else {
      setComboForm({ name: combo.name, price: combo.price, active: combo.active !== false, slots: combo.slots?.length ? combo.slots.map(s=>({ allowedSizes:[], ...s })) : [{ id:'slot_'+Date.now(), label:'', itemIds:[], allowedSizes:[] }] });
    }
    setComboModal(combo);
  };

  const saveCombo = async () => {
    if (!comboForm.name || !comboForm.price) { toast.error('Name and price required'); return; }
    if (comboForm.slots.some(s => !s.label || !s.itemIds.length)) { toast.error('Each slot needs a label and at least 1 item'); return; }
    setSaving(true);
    try {
      const data = { name: comboForm.name, price: +comboForm.price, active: comboForm.active, slots: comboForm.slots, updatedAt: serverTimestamp() };
      if (comboModal === 'add') {
        const id = comboForm.name.toLowerCase().replace(/\s+/g,'_') + '_' + Date.now();
        await setDoc(doc(db,'combos', id), { ...data, createdAt: serverTimestamp() });
        toast.success('Combo added!');
      } else {
        await updateDoc(doc(db,'combos', comboModal.id), data);
        toast.success('Combo updated!');
      }
      setComboModal(null);
    } catch(e) { toast.error(e.message); }
    setSaving(false);
  };

  const toggleCombo = async (id, current) => { await updateDoc(doc(db,'combos',id), { active: !current }); toast.success(!current ? 'Activated' : 'Paused'); };
  const deleteCombo = async (id) => { if (!window.confirm('Delete this combo?')) return; await deleteDoc(doc(db,'combos',id)); toast.success('Deleted'); };

  const addSlot = () => setComboForm(f => ({ ...f, slots: [...f.slots, { id:'slot_'+Date.now(), label:'', itemIds:[], allowedSizes:[] }] }));
  const removeSlot = (idx) => setComboForm(f => ({ ...f, slots: f.slots.filter((_,i)=>i!==idx) }));
  const updateSlotLabel = (idx, label) => setComboForm(f => ({ ...f, slots: f.slots.map((s,i)=>i===idx?{...s,label}:s) }));
  const toggleSlotItem = (idx, itemId) => setComboForm(f => ({
    ...f, slots: f.slots.map((s,i) => i===idx ? { ...s, itemIds: s.itemIds.includes(itemId) ? s.itemIds.filter(x=>x!==itemId) : [...s.itemIds, itemId] } : s)
  }));
  const toggleSlotSize = (idx, sizeLabel) => setComboForm(f => ({
    ...f, slots: f.slots.map((s,i) => {
      if (i !== idx) return s;
      const cur = s.allowedSizes || [];
      return { ...s, allowedSizes: cur.includes(sizeLabel) ? cur.filter(x=>x!==sizeLabel) : [...cur, sizeLabel] };
    })
  }));

  // ── SETTINGS ───────────────────────────────────────────────
  const saveSettings = async() => {
    setSaving(true);
    try {
      const { menuCategories, upsellItems, ...rest } = settingsForm; // these two are managed on their own tabs
      if (rest.loyaltyExpiryDays !== undefined) rest.loyaltyExpiryDays = Math.max(0, Math.floor(Number(rest.loyaltyExpiryDays) || 0));
      await setDoc(doc(db,'config','settings'),{...rest,updatedAt:serverTimestamp()},{ merge:true });
      toast.success('Settings saved!');
    }
    catch(e){toast.error(e.message);}
    setSaving(false);
  };

  // ── CUSTOMERS: import from Excel (name, phone, address) / download what is saved ──
  const loadCustomers = async () => {
    setCustomersLoading(true);
    try {
      const snap = await getDocs(collection(db, 'customers'));
      const list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setCustomers(list); setCustomersLoading(false);
      return list;
    } catch (e) { toast.error('Could not load customers: ' + e.message); setCustomersLoading(false); return []; }
  };
  useEffect(() => { if (tab === 'Customers') loadCustomers(); }, [tab]); // eslint-disable-line

  const downloadCustomersExcel = async () => {
    try {
      const XLSX = await import('xlsx');
      const rows = customers.length ? buildCustomerExportRows(customers) : SAMPLE_CUSTOMER_ROWS;
      const ws = XLSX.utils.json_to_sheet(rows);
      ws['!cols'] = [{ wch: 28 }, { wch: 16 }, { wch: 50 }, { wch: 26 }];
      // keep phone numbers as text so Excel does not turn them into 9.87E+09
      Object.keys(ws).forEach(k => { if (/^B\d+$/.test(k) && k !== 'B1' && ws[k].t === 'n') { ws[k].t = 's'; ws[k].v = String(ws[k].v); } });
      const info = XLSX.utils.aoa_to_sheet(CUSTOMER_INSTRUCTIONS); info['!cols'] = [{ wch: 110 }];
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, CUSTOMER_SHEET);
      XLSX.utils.book_append_sheet(wb, info, 'Instructions');
      XLSX.writeFile(wb, customers.length ? `VIP-Pizza-Customers-${new Date().toISOString().slice(0,10)}.xlsx` : 'VIP-Pizza-Customers-Template.xlsx');
    } catch (e) { toast.error('Could not create the Excel file: ' + e.message); }
  };

  const handleCustomerFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    try {
      const XLSX = await import('xlsx');
      const wb = XLSX.read(await file.arrayBuffer(), { type: 'array' });
      const ws = wb.Sheets[CUSTOMER_SHEET] || wb.Sheets[wb.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(ws, { defval: '' });
      const existing = await loadCustomers(); // always compare with the latest saved customers
      setCustomerPreview({ fileName: file.name, ...parseCustomerRows(rows, existing, { overwrite: custOverwrite }) });
    } catch (err) { toast.error('Could not read that file: ' + err.message); }
  };

  const applyCustomerImport = async () => {
    const p = customerPreview;
    if (!p || (!p.creates.length && !p.updates.length)) return;
    setCustImporting(true);
    try {
      const ops = [
        ...p.creates.map(c => ({ kind: 'set', ref: doc(collection(db, 'customers')), data: { phone: c.phone, name: c.name, address: c.address, email: c.email, source: 'import', createdAt: serverTimestamp() } })),
        ...p.updates.map(u => ({ kind: 'update', ref: doc(db, 'customers', u.id), data: { ...u.data, updatedAt: serverTimestamp() } })),
      ];
      for (let i = 0; i < ops.length; i += 400) {
        const batch = writeBatch(db);
        ops.slice(i, i + 400).forEach(o => (o.kind === 'set' ? batch.set(o.ref, o.data) : batch.update(o.ref, o.data)));
        await batch.commit();
      }
      toast.success(`Imported ${p.creates.length} new customer${p.creates.length===1?'':'s'}${p.updates.length ? `, updated ${p.updates.length}` : ''}`);
      setCustomerPreview(null);
      await loadCustomers();
    } catch (err) { toast.error('Import failed: ' + err.message); }
    setCustImporting(false);
  };

  // ── CATEGORIES (an ordered list kept in config/settings → menuCategories; items belong to a category by name) ──
  const sameCat = (a, b) => String(a||'').trim().toLowerCase() === String(b||'').trim().toLowerCase();
  const categoryList = (() => {
    const out = []; const seen = new Set();
    const add = (n) => { const name = String(n||'').trim(); const k = name.toLowerCase(); if (name && !seen.has(k)) { seen.add(k); out.push(name); } };
    (settings.menuCategories || []).forEach(add);
    items.forEach(i => add(i.cat));
    return out;
  })();
  const itemsInCat = (name) => items.filter(i => sameCat(i.cat, name));
  const saveCategoryList = (list) => setDoc(doc(db,'config','settings'), { menuCategories: list, updatedAt: serverTimestamp() }, { merge: true });

  // move every item (and weekly-offer category filter) from one category name to another, in chunks of 400
  const recategorize = async (oldName, newName) => {
    const ops = [];
    itemsInCat(oldName).forEach(i => ops.push({ ref: doc(db,'menu',i.id), data: { cat: newName, updatedAt: serverTimestamp() } }));
    weeklyOffers.forEach(o => {
      if ((o.catFilter||[]).some(c => sameCat(c, oldName))) {
        const next = [...new Set((o.catFilter||[]).map(c => sameCat(c, oldName) ? newName : c))];
        ops.push({ ref: doc(db,'weeklyOffers',o.id), data: { catFilter: next } });
      }
    });
    for (let i = 0; i < ops.length; i += 400) {
      const batch = writeBatch(db);
      ops.slice(i, i + 400).forEach(o => batch.update(o.ref, o.data));
      await batch.commit();
    }
  };

  const addCategory = async () => {
    const name = newCategory.trim();
    if (!name) { toast.error('Enter a category name'); return; }
    if (categoryList.some(c => sameCat(c, name))) { toast.error('That category already exists'); return; }
    setCatBusy(true);
    try { await saveCategoryList([...categoryList, name]); setNewCategory(''); toast.success('Category added'); }
    catch (e) { toast.error(e.message); }
    setCatBusy(false);
  };
  const renameCategory = async () => {
    const oldName = renaming.old, name = renaming.value.trim();
    if (!name) { toast.error('Enter a name'); return; }
    if (name === oldName) { setRenaming(null); return; }
    if (categoryList.some(c => sameCat(c, name) && !sameCat(c, oldName))) {
      toast.error('Another category already has that name. To merge two categories, delete one and move its items into the other.'); return;
    }
    setCatBusy(true);
    try {
      await recategorize(oldName, name);
      await saveCategoryList(categoryList.map(c => c === oldName ? name : c));
      toast.success('Category renamed'); setRenaming(null);
    } catch (e) { toast.error(e.message); }
    setCatBusy(false);
  };
  const deleteCategory = async () => {
    const { name, moveTo } = deletingCat; const count = itemsInCat(name).length;
    if (count > 0 && !moveTo) { toast.error('Choose a category to move the items into'); return; }
    setCatBusy(true);
    try {
      if (count > 0) await recategorize(name, moveTo);
      await saveCategoryList(categoryList.filter(c => c !== name));
      toast.success(count ? `Moved ${count} item${count>1?'s':''} and deleted "${name}"` : `Deleted "${name}"`);
      setDeletingCat(null);
    } catch (e) { toast.error(e.message); }
    setCatBusy(false);
  };
  const moveCategory = async (idx, dir) => {
    const j = idx + dir; if (j < 0 || j >= categoryList.length) return;
    const list = [...categoryList]; [list[idx], list[j]] = [list[j], list[idx]];
    try { await saveCategoryList(list); } catch (e) { toast.error(e.message); }
  };

  // ── SUGGESTED ITEMS (shown in the Print KOT popup; kept in config/settings → upsellItems) ──
  const upsellList = upsellDraft ?? (Array.isArray(settings.upsellItems) ? settings.upsellItems : DEFAULT_UPSELL_ITEMS);
  const editUpsell = (idx, field, val) => setUpsellDraft(upsellList.map((u,i) => i===idx ? { ...u, [field]: val } : u));
  const removeUpsell = (idx) => setUpsellDraft(upsellList.filter((_,i) => i!==idx));
  const addUpsell = () => {
    const name = newUpsell.name.trim(); const price = Number(newUpsell.price);
    if (!name) { toast.error('Enter an item name'); return; }
    if (newUpsell.price === '' || !Number.isFinite(price) || price < 0) { toast.error('Enter a valid price'); return; }
    setUpsellDraft([...upsellList, { id: 'u_' + Date.now(), emoji: newUpsell.emoji || '🍽️', name, price }]);
    setNewUpsell({ emoji:'🍽️', name:'', price:'' });
  };
  const saveUpsell = async () => {
    const cleaned = [];
    for (const u of upsellList) {
      const name = String(u.name||'').trim(); const price = Number(u.price);
      if (!name || u.price === '' || !Number.isFinite(price) || price < 0) { toast.error('Every suggested item needs a name and a valid price'); return; }
      cleaned.push({ id: u.id, emoji: u.emoji || '🍽️', name, price });
    }
    setSaving(true);
    try { await setDoc(doc(db,'config','settings'), { upsellItems: cleaned, updatedAt: serverTimestamp() }, { merge: true }); setUpsellDraft(null); toast.success('Suggested items saved'); }
    catch (e) { toast.error(e.message); }
    setSaving(false);
  };

  // ── Add-on options that have no name (they would print as "undefined") ──
  const unnamedOptions = addonGroups.flatMap(g => (g.options||[]).filter(o => !String(o.name||'').trim()));
  const fixOptionNames = async () => {
    try {
      for (const g of addonGroups) {
        if (!(g.options||[]).some(o => !String(o.name||'').trim())) continue;
        const options = g.options.map(o => String(o.name||'').trim() ? o : { ...o, name: optionLabel(o) });
        await updateDoc(doc(db,'addonGroups',g.id), { options });
      }
      toast.success('Add-on names fixed');
    } catch (e) { toast.error(e.message); }
  };

  // ── Excel: download current menu prices / upload an edited sheet ──
  const downloadMenuExcel = async () => {
    if (!items.length) { toast.error('No menu items to export yet'); return; }
    try {
      const XLSX = await import('xlsx');
      const ws = XLSX.utils.json_to_sheet(buildExportRows(items));
      ws['!cols'] = [{ wch: 24 }, { wch: 30 }, { wch: 22 }, { wch: 12 }, { wch: 12 }, { wch: 12 }];
      const info = XLSX.utils.aoa_to_sheet(INSTRUCTION_ROWS);
      info['!cols'] = [{ wch: 120 }];
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, SHEET_NAME);
      XLSX.utils.book_append_sheet(wb, info, 'Instructions');
      XLSX.writeFile(wb, `VIP-Pizza-Menu-Prices-${new Date().toISOString().slice(0,10)}.xlsx`);
      toast.success('Menu downloaded');
    } catch (e) { toast.error('Could not create the Excel file: ' + e.message); }
  };

  const handleExcelFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow choosing the same file again later
    if (!file) return;
    try {
      const XLSX = await import('xlsx');
      const wb = XLSX.read(await file.arrayBuffer(), { type: 'array' });
      const ws = wb.Sheets[SHEET_NAME] || wb.Sheets[wb.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(ws, { defval: '' });
      setImportPreview({ fileName: file.name, ...parseImportRows(rows, items) });
    } catch (err) { toast.error('Could not read that file: ' + err.message); }
  };

  const applyImport = async () => {
    if (!importPreview?.updates.length) return;
    setImporting(true);
    try {
      const list = importPreview.updates;
      for (let i = 0; i < list.length; i += 400) {
        const batch = writeBatch(db);
        list.slice(i, i + 400).forEach(u => batch.update(doc(db, 'menu', u.id), { ...u.data, updatedAt: serverTimestamp() }));
        await batch.commit();
      }
      toast.success(`Updated prices for ${list.length} item${list.length>1?'s':''}`);
      setImportPreview(null);
    } catch (err) { toast.error('Upload failed: ' + err.message); }
    setImporting(false);
  };

  const allCats = ['all', ...Array.from(new Map(items.filter(i=>i.cat).map(i=>[i.cat.trim().toLowerCase(), i.cat.trim()])).values())];
  const filteredItems = items.filter(i => {
    const matchSearch = !search||i.name?.toLowerCase().includes(search.toLowerCase());
    const matchCat    = filterCat==='all'||(i.cat||'').trim().toLowerCase()===filterCat.trim().toLowerCase();
    return matchSearch&&matchCat;
  });

  const priceInput = (groupId, optionId, sizeKey, currentVal) => (
    <input type="number" defaultValue={currentVal||0} min="0"
      onBlur={e => updateOptionPrice(groupId, optionId, sizeKey, e.target.value)}
      style={{ width:65, border:'1px solid #ddd', borderRadius:6, padding:'4px 6px', fontSize:12, textAlign:'center' }} />
  );

  return (
    <div>
      <div style={{ display:'flex', gap:6, marginBottom:18, flexWrap:'wrap' }}>
        {TABS.map(t=>(
          <button key={t} onClick={()=>setTab(t)} style={{
            padding:'7px 16px', borderRadius:20, fontSize:13, cursor:'pointer',
            border:'1px solid', fontWeight:tab===t?600:400,
            borderColor:tab===t?'#D85A30':'#ddd', background:tab===t?'#D85A30':'#fff', color:tab===t?'#fff':'#555',
          }}>{t}</button>
        ))}
      </div>

      {/* ── MENU ITEMS ── */}
      {tab==='Menu Items' && (
        <div>
          <div style={{ display:'flex', gap:10, marginBottom:14, flexWrap:'wrap', alignItems:'center' }}>
            <input className="input" placeholder="🔍 Search…" value={search} onChange={e=>setSearch(e.target.value)} style={{ width:180 }} />
            <select className="input" style={{ width:160 }} value={filterCat} onChange={e=>setFilterCat(e.target.value)}>
              {allCats.map(c=><option key={c} value={c}>{c==='all'?'All categories':c}</option>)}
            </select>
            <div style={{ marginLeft:'auto', display:'flex', gap:8 }}>
              <button className="btn" onClick={downloadMenuExcel}
                style={{ fontSize:12, padding:'5px 12px' }}>⬇ Download Excel</button>
              <button className="btn" onClick={()=>fileInputRef.current?.click()}
                style={{ fontSize:12, padding:'5px 12px' }}>⬆ Upload Excel</button>
              <input ref={fileInputRef} type="file" accept=".xlsx,.xls,.csv" onChange={handleExcelFile} style={{ display:'none' }} />
              <button className="btn" onClick={seedMenu} disabled={seeding}
                style={{ fontSize:12, padding:'5px 12px', background:'#EAF3DE', color:'#3B6D11', border:'1px solid #3B6D11' }}>
                {seeding?'Loading…':'📥 Load from Menu'}
              </button>
              <button className="btn btn-primary" style={{ fontSize:12, padding:'5px 12px' }}
                onClick={()=>{ setItemForm({name:'',cat:'',basePrice:'',emoji:'🍕',sizes:'pizza',hasAddon:true,available:true,sizePrices:{}}); setItemModal('add'); }}>
                + Add Item
              </button>
            </div>
          </div>
          <div style={{ display:'flex', gap:10, marginBottom:14, flexWrap:'wrap' }}>
            {[['Total',items.length],['Available',items.filter(i=>i.available!==false).length],['Hidden',items.filter(i=>i.available===false).length],['Categories',new Set(items.map(i=>i.cat)).size]].map(([l,v])=>(
              <div key={l} className="metric" style={{ flex:1, minWidth:100 }}><div className="metric-label">{l}</div><div className="metric-val" style={{ fontSize:18 }}>{v}</div></div>
            ))}
          </div>
          <div className="card" style={{ padding:0, overflow:'hidden' }}>
            <table className="table">
              <thead><tr><th>Item</th><th>Category</th><th>Base Price</th><th>Quick Update Price</th><th>Status</th><th>Actions</th></tr></thead>
              <tbody>
                {filteredItems.length===0&&<tr><td colSpan={6} style={{ textAlign:'center',color:'#bbb',padding:30 }}>No items. Click "Load from Menu" to import all 50+ items, then add "Bakery" items with + Add Item.</td></tr>}
                {filteredItems.map(item=>(
                  <tr key={item.id} style={{ opacity:item.available===false?0.5:1 }}>
                    <td><span style={{ fontSize:18,marginRight:6 }}>{item.emoji}</span><span style={{ fontWeight:500 }}>{item.name}</span></td>
                    <td><span className="tag tag-blue">{item.cat}</span></td>
                    <td style={{ fontWeight:600,color:'#D85A30' }}>
                      ₹{item.basePrice}
                      {(SIZES[item.sizes]||SIZES.single).length>1 && (
                        <div style={{ fontSize:10, color:'#aaa', fontWeight:400, marginTop:2 }}>
                          {(SIZES[item.sizes]||SIZES.single).map(s=>{
                            const ov=item.sizePrices?.[s.label];
                            const eff=ov!=null&&ov!==''?ov:autoSizePrice(item.basePrice,s.mult);
                            return `${s.label} ₹${fmt(eff)}`;
                          }).join(' · ')}
                        </div>
                      )}
                    </td>
                    <td>
                      <div style={{ display:'flex', gap:5, alignItems:'center' }}>
                        <input type="number" defaultValue={item.basePrice} id={`price-${item.id}`} style={{ width:75,border:'1px solid #ddd',borderRadius:6,padding:'4px 7px',fontSize:12 }} />
                        <button className="btn" style={{ fontSize:11,padding:'4px 9px',background:'#EAF3DE',color:'#3B6D11',border:'none' }}
                          onClick={()=>{ const v=document.getElementById(`price-${item.id}`).value; updatePrice(item.id,v); }}>✓</button>
                      </div>
                    </td>
                    <td><span className={`tag ${item.available!==false?'tag-green':'tag-red'}`} style={{ cursor:'pointer' }} onClick={()=>toggleAvailable(item.id,item.available!==false)}>{item.available!==false?'✅ Visible':'❌ Hidden'}</span></td>
                    <td>
                      <div style={{ display:'flex', gap:5 }}>
                        <button className="btn" style={{ fontSize:11,padding:'4px 9px' }} onClick={()=>{ setItemForm({sizePrices:{}, ...item}); setItemModal(item); }}>✏ Edit</button>
                        <button className="btn btn-danger" style={{ fontSize:11,padding:'4px 9px' }} onClick={()=>deleteItem(item.id,item.name)}>🗑</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── ADD-ONS ── */}
      {/* ── CATEGORIES ── */}
      {tab==='Categories' && (
        <div>
          <div style={{ fontSize:13, color:'#888', marginBottom:12 }}>
            Categories are the tabs on the billing screen, shown in this order. Renaming a category updates all its items.
          </div>
          <div style={{ display:'flex', gap:8, marginBottom:14, flexWrap:'wrap' }}>
            <input className="input" placeholder="New category name, e.g. Desserts" value={newCategory}
              onChange={e=>setNewCategory(e.target.value)} onKeyDown={e=>{ if(e.key==='Enter') addCategory(); }} style={{ maxWidth:280 }} />
            <button className="btn btn-primary" onClick={addCategory} disabled={catBusy}>+ Add Category</button>
          </div>
          <div className="card" style={{ padding:0, overflow:'hidden' }}>
            <table className="table">
              <thead><tr><th style={{ width:90 }}>Order</th><th>Category</th><th style={{ width:90 }}>Items</th><th style={{ width:210 }}></th></tr></thead>
              <tbody>
                {categoryList.map((name, idx) => (
                  <tr key={name}>
                    <td>
                      <button className="btn" style={{ fontSize:11, padding:'2px 8px' }} disabled={idx===0} onClick={()=>moveCategory(idx,-1)}>↑</button>{' '}
                      <button className="btn" style={{ fontSize:11, padding:'2px 8px' }} disabled={idx===categoryList.length-1} onClick={()=>moveCategory(idx,1)}>↓</button>
                    </td>
                    <td style={{ fontWeight:500 }}>
                      {renaming?.old===name
                        ? <input className="input" autoFocus value={renaming.value} onChange={e=>setRenaming(r=>({...r,value:e.target.value}))}
                            onKeyDown={e=>{ if(e.key==='Enter') renameCategory(); if(e.key==='Escape') setRenaming(null); }} style={{ maxWidth:260 }} />
                        : name}
                    </td>
                    <td>{itemsInCat(name).length}</td>
                    <td style={{ textAlign:'right' }}>
                      {renaming?.old===name ? (
                        <>
                          <button className="btn btn-primary" style={{ fontSize:11, padding:'4px 10px' }} onClick={renameCategory} disabled={catBusy}>{catBusy?'Saving…':'Save'}</button>{' '}
                          <button className="btn" style={{ fontSize:11, padding:'4px 10px' }} onClick={()=>setRenaming(null)}>Cancel</button>
                        </>
                      ) : (
                        <>
                          <button className="btn" style={{ fontSize:11, padding:'4px 10px' }} onClick={()=>setRenaming({ old:name, value:name })}>✏ Rename</button>{' '}
                          <button className="btn btn-danger" style={{ fontSize:11, padding:'4px 10px' }} onClick={()=>setDeletingCat({ name, moveTo:'' })}>🗑 Delete</button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
                {!categoryList.length && <tr><td colSpan={4} style={{ textAlign:'center', color:'#bbb', padding:24 }}>No categories yet — add one above.</td></tr>}
              </tbody>
            </table>
          </div>
          <div style={{ fontSize:11, color:'#aaa', marginTop:8 }}>Combos always appear as their own "Combo" tab. A category with no items is hidden on the billing screen until you add an item to it.</div>
        </div>
      )}

      {/* ── CUSTOMERS ── */}
      {tab==='Customers' && (
        <div>
          <div className="card" style={{ marginBottom:14 }}>
            <div className="sec-title">Import customers from Excel</div>
            <div style={{ fontSize:13, color:'#666', marginBottom:12 }}>
              Bring in the customers from your old software. The sheet needs <b>Name</b>, <b>Phone</b> and <b>Address</b> columns (Email is optional).
              A phone number that is already saved is never added twice, and you see a preview before anything is saved.
            </div>
            <div style={{ display:'flex', gap:8, flexWrap:'wrap', alignItems:'center' }}>
              <button className="btn" onClick={downloadCustomersExcel}>⬇ Download Excel {customers.length ? '(saved customers)' : '(blank template)'}</button>
              <button className="btn btn-primary" onClick={()=>custFileRef.current?.click()}>⬆ Upload Excel</button>
              <input ref={custFileRef} type="file" accept=".xlsx,.xls,.csv" onChange={handleCustomerFile} style={{ display:'none' }} />
            </div>
            <label style={{ display:'flex', gap:7, alignItems:'center', fontSize:12, color:'#666', marginTop:12, cursor:'pointer' }}>
              <input type="checkbox" checked={custOverwrite} onChange={e=>setCustOverwrite(e.target.checked)} />
              Replace the details of customers who are already saved (otherwise only blank fields are filled in)
            </label>
            <div style={{ fontSize:12, color:'#aaa', marginTop:10 }}>{customersLoading ? 'Loading customers…' : `${customers.length} customer${customers.length===1?'':'s'} saved`}</div>
          </div>
        </div>
      )}

      {/* ── SUGGESTED ITEMS ── */}
      {tab==='Suggested Items' && (
        <div>
          <div style={{ fontSize:13, color:'#888', marginBottom:12 }}>
            These are offered to the customer in the popup that opens when staff click <b>Print KOT</b>. If you remove them all, the popup is skipped.
          </div>
          <div className="card" style={{ padding:0, overflow:'hidden', marginBottom:14 }}>
            <table className="table">
              <thead><tr><th style={{ width:70 }}>Icon</th><th>Name</th><th style={{ width:130 }}>Price (₹)</th><th style={{ width:80 }}></th></tr></thead>
              <tbody>
                {upsellList.map((u, idx) => (
                  <tr key={u.id || idx}>
                    <td><input className="input" value={u.emoji||''} onChange={e=>editUpsell(idx,'emoji',e.target.value)} style={{ width:56, textAlign:'center' }} /></td>
                    <td><input className="input" value={u.name||''} onChange={e=>editUpsell(idx,'name',e.target.value)} /></td>
                    <td><input className="input" type="number" min="0" value={u.price ?? ''} onChange={e=>editUpsell(idx,'price',e.target.value)} /></td>
                    <td style={{ textAlign:'right' }}><button className="btn btn-danger" style={{ fontSize:11, padding:'4px 10px' }} onClick={()=>removeUpsell(idx)}>🗑</button></td>
                  </tr>
                ))}
                {!upsellList.length && <tr><td colSpan={4} style={{ textAlign:'center', color:'#bbb', padding:24 }}>No suggested items — the popup will be skipped.</td></tr>}
                <tr style={{ background:'#fafafa' }}>
                  <td><input className="input" value={newUpsell.emoji} onChange={e=>setNewUpsell(n=>({...n,emoji:e.target.value}))} style={{ width:56, textAlign:'center' }} /></td>
                  <td><input className="input" placeholder="New item name" value={newUpsell.name} onChange={e=>setNewUpsell(n=>({...n,name:e.target.value}))} onKeyDown={e=>{ if(e.key==='Enter') addUpsell(); }} /></td>
                  <td><input className="input" type="number" min="0" placeholder="Price" value={newUpsell.price} onChange={e=>setNewUpsell(n=>({...n,price:e.target.value}))} onKeyDown={e=>{ if(e.key==='Enter') addUpsell(); }} /></td>
                  <td style={{ textAlign:'right' }}><button className="btn" style={{ fontSize:11, padding:'4px 10px' }} onClick={addUpsell}>+ Add</button></td>
                </tr>
              </tbody>
            </table>
          </div>
          <div style={{ display:'flex', gap:8, alignItems:'center', flexWrap:'wrap' }}>
            <button className="btn btn-primary" onClick={saveUpsell} disabled={saving || upsellDraft===null}>{saving?'Saving…':'💾 Save Suggested Items'}</button>
            {upsellDraft!==null && <button className="btn" onClick={()=>setUpsellDraft(null)}>Discard changes</button>}
            <button className="btn" onClick={()=>setUpsellDraft(DEFAULT_UPSELL_ITEMS)}>Reset to default list</button>
            {upsellDraft!==null && <span style={{ fontSize:12, color:'#b3702a' }}>Unsaved changes</span>}
          </div>
        </div>
      )}

      {tab==='Add-ons' && (
        <div>
          {unnamedOptions.length>0 && (
            <div style={{ background:'#FFF6E0', border:'1px solid #f0d89a', borderRadius:8, padding:'10px 14px', marginBottom:12, fontSize:13, color:'#7a5a00', display:'flex', gap:10, alignItems:'center', flexWrap:'wrap' }}>
              ⚠️ {unnamedOptions.length} add-on option{unnamedOptions.length>1?'s have':' has'} no name saved (this is what printed as “undefined”).
              <button className="btn btn-primary" style={{ fontSize:12, padding:'4px 12px' }} onClick={fixOptionNames}>Fix names</button>
            </div>
          )}
          <div style={{ display:'flex', justifyContent:'flex-end', marginBottom:14 }}>
            <button className="btn btn-primary" style={{ fontSize:12,padding:'5px 12px' }} onClick={()=>setAddonModal('newGroup')}>+ New Addon Group</button>
          </div>
          {addonGroups.length===0&&<div style={{ textAlign:'center',padding:40,color:'#bbb',fontSize:13 }}>No addon groups yet.</div>}
          {addonGroups.map(group=>(
            <div key={group.id} className="card" style={{ marginBottom:16 }}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:12 }}>
                <div>
                  <div style={{ fontWeight:600, fontSize:14 }}>{group.name}</div>
                  <div style={{ fontSize:12, color:'#888', marginTop:2 }}>
                    Selection: <span className={`tag ${group.selectionType==='single'?'tag-blue':'tag-green'}`}>{group.selectionType==='single'?'Choose ONE':'Choose MANY'}</span>
                    &nbsp; Applies to: {(group.appliesTo||[]).join(', ')}
                  </div>
                </div>
                <button className="btn btn-primary" style={{ fontSize:12,padding:'4px 10px' }} onClick={()=>{ setEditingGroup(group.id); setAddonModal('newOption'); }}>+ Add Option</button>
              </div>
              <div style={{ overflowX:'auto' }}>
                <table className="table">
                  <thead><tr><th style={{ minWidth:150 }}>Option</th><th style={{ textAlign:'center' }}>Personal (₹)</th><th style={{ textAlign:'center' }}>Share (₹)</th><th style={{ textAlign:'center' }}>Family (₹)</th><th style={{ textAlign:'center' }}>Status</th><th>Action</th></tr></thead>
                  <tbody>
                    {(group.options||[]).length===0&&<tr><td colSpan={6} style={{ textAlign:'center',color:'#bbb',padding:16 }}>No options yet</td></tr>}
                    {(group.options||[]).map(opt=>(
                      <tr key={opt.id} style={{ opacity:opt.active===false?0.5:1 }}>
                        <td style={{ fontWeight:500 }}>{optionLabel(opt)}</td>
                        <td style={{ textAlign:'center' }}>{priceInput(group.id,opt.id,'personalPrice',opt.personalPrice)}</td>
                        <td style={{ textAlign:'center' }}>{priceInput(group.id,opt.id,'sharePrice',opt.sharePrice)}</td>
                        <td style={{ textAlign:'center' }}>{priceInput(group.id,opt.id,'familyPrice',opt.familyPrice)}</td>
                        <td style={{ textAlign:'center' }}><span className={`tag ${opt.active!==false?'tag-green':'tag-red'}`} style={{ cursor:'pointer' }} onClick={()=>toggleOption(group.id,opt.id,opt.active!==false)}>{opt.active!==false?'ON':'OFF'}</span></td>
                        <td><button className="btn btn-danger" style={{ fontSize:11,padding:'3px 8px' }} onClick={()=>deleteOption(group.id,opt.id)}>🗑</button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div style={{ fontSize:11,color:'#aaa',marginTop:8 }}>💡 Click a price field and click away to save automatically</div>
            </div>
          ))}
        </div>
      )}

      {/* ── WEEKLY OFFERS ── */}
      {tab==='Weekly Offers' && (
        <div>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:14 }}>
            <div style={{ fontSize:13,color:'#888' }}>Day-specific offers (e.g. Tuesday/Thursday/Saturday specials). Fully editable — no code changes needed.</div>
            <div style={{ display:'flex', gap:8 }}>
              {!weeklyOffers.length && (
                <button className="btn" disabled={seedingOffers} onClick={seedWeeklyOffers}
                  style={{ fontSize:12, padding:'5px 12px', background:'#EAF3DE', color:'#3B6D11', border:'1px solid #3B6D11' }}>
                  {seedingOffers ? 'Loading…' : '📥 Load Default Offers'}
                </button>
              )}
              <button className="btn btn-primary" style={{ fontSize:12,padding:'5px 12px' }} onClick={()=>openWeeklyEdit('add')}>+ Add Weekly Offer</button>
            </div>
          </div>
          {!weeklyOffers.length && <div style={{ textAlign:'center',padding:40,color:'#bbb' }}>No weekly offers yet. Click "Load Default Offers" for Tue/Thu/Sat, or add your own.</div>}
          {weeklyOffers.sort((a,b)=>a.dayOfWeek-b.dayOfWeek).map(o=>(
            <div key={o.id} className="card" style={{ marginBottom:10,padding:'14px 16px' }}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start' }}>
                <div style={{ flex:1 }}>
                  <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:6, flexWrap:'wrap' }}>
                    <span style={{ fontWeight:600,fontSize:14 }}>{o.name}</span>
                    <span className="tag tag-blue">{DAYS[o.dayOfWeek]}</span>
                    <span className={`tag ${o.active!==false?'tag-green':'tag-gray'}`}>{o.active!==false?'Active':'Paused'}</span>
                    <span className="tag tag-amber">{o.type==='fixed_price'?`Fixed ₹${o.fixedPrice}`:'Buy 1 Get 1'}</span>
                  </div>
                  <div style={{ fontSize:13,color:'#666' }}>{o.description}</div>
                  {o.type==='fixed_price' && <div style={{ fontSize:12,color:'#aaa',marginTop:4 }}>Size: {o.sizeFilter}{o.catFilter?.length?` | Categories: ${o.catFilter.join(', ')}`:''}</div>}
                  {o.type==='buy1get1' && <div style={{ fontSize:12,color:'#aaa',marginTop:4 }}>{(o.rules||[]).map(r=>`${r.size} ≥₹${r.minPrice}`).join(' | ')}</div>}
                </div>
                <div style={{ display:'flex', gap:6, marginLeft:12 }}>
                  <button className="btn" style={{ fontSize:11,padding:'4px 9px' }} onClick={()=>toggleWeeklyOffer(o.id,o.active!==false)}>{o.active!==false?'⏸ Pause':'▶ Activate'}</button>
                  <button className="btn" style={{ fontSize:11,padding:'4px 9px' }} onClick={()=>openWeeklyEdit(o)}>✏ Edit</button>
                  <button className="btn btn-danger" style={{ fontSize:11,padding:'4px 9px' }} onClick={()=>deleteWeeklyOffer(o.id)}>🗑</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── COMBOS ── */}
      {tab==='Combos' && (
        <div>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:14 }}>
            <div style={{ fontSize:13,color:'#888' }}>Build combo meals with slots — customer picks one item per slot at a fixed price.</div>
            <button className="btn btn-primary" style={{ fontSize:12,padding:'5px 12px' }} onClick={()=>openComboEdit('add')}>+ Add Combo</button>
          </div>
          {!combos.length && <div style={{ textAlign:'center',padding:40,color:'#bbb' }}>No combos yet. Click "+ Add Combo" to create one (e.g. "Combo A" — 1 Pizza + 1 Drink + 1 Side).</div>}
          {combos.map(c=>(
            <div key={c.id} className="card" style={{ marginBottom:10,padding:'14px 16px' }}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start' }}>
                <div style={{ flex:1 }}>
                  <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:6 }}>
                    <span style={{ fontWeight:600,fontSize:14 }}>🍱 {c.name}</span>
                    <span className="tag tag-amber">₹{c.price}</span>
                    <span className={`tag ${c.active!==false?'tag-green':'tag-gray'}`}>{c.active!==false?'Active':'Paused'}</span>
                  </div>
                  <div style={{ fontSize:12,color:'#888' }}>{(c.slots||[]).map(s=>`${s.label} (${s.itemIds.length} options)`).join(' · ')}</div>
                </div>
                <div style={{ display:'flex', gap:6, marginLeft:12 }}>
                  <button className="btn" style={{ fontSize:11,padding:'4px 9px' }} onClick={()=>toggleCombo(c.id,c.active!==false)}>{c.active!==false?'⏸ Pause':'▶ Activate'}</button>
                  <button className="btn" style={{ fontSize:11,padding:'4px 9px' }} onClick={()=>openComboEdit(c)}>✏ Edit</button>
                  <button className="btn btn-danger" style={{ fontSize:11,padding:'4px 9px' }} onClick={()=>deleteCombo(c.id)}>🗑</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── COUPON OFFERS ── */}
      {tab==='Offers' && (
        <div>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:14 }}>
            <div style={{ fontSize:13,color:'#888' }}>Coupon-style discount offers (% or flat ₹ off).</div>
            <button className="btn btn-primary" style={{ fontSize:12,padding:'5px 12px' }} onClick={()=>{ setOfferForm({title:'',description:'',type:'percentage',value:'',minOrder:'',code:'',active:true}); setOfferModal('add'); }}>+ Add Offer</button>
          </div>
          {!offers.length&&<div style={{ textAlign:'center',padding:40,color:'#bbb' }}>No coupon offers yet.</div>}
          {offers.map(o=>(
            <div key={o.id} className="card" style={{ marginBottom:10,padding:'14px 16px' }}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start' }}>
                <div style={{ flex:1 }}>
                  <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:6, flexWrap:'wrap' }}>
                    <span style={{ fontWeight:600,fontSize:14 }}>{o.title}</span>
                    <span className={`tag ${o.active?'tag-green':'tag-gray'}`}>{o.active?'Active':'Paused'}</span>
                    <span className="tag tag-blue">{o.type==='percentage'?`${o.value}% OFF`:`₹${o.value} OFF`}</span>
                    {o.code&&<span className="tag tag-amber">Code: {o.code}</span>}
                  </div>
                  <div style={{ fontSize:13,color:'#666',marginBottom:4 }}>{o.description}</div>
                </div>
                <div style={{ display:'flex', gap:6, marginLeft:12 }}>
                  <button className="btn" style={{ fontSize:11,padding:'4px 9px' }} onClick={()=>toggleOffer(o.id,o.active)}>{o.active?'⏸ Pause':'▶ Activate'}</button>
                  <button className="btn" style={{ fontSize:11,padding:'4px 9px' }} onClick={()=>{ setOfferForm({...o}); setOfferModal(o); }}>✏</button>
                  <button className="btn btn-danger" style={{ fontSize:11,padding:'4px 9px' }} onClick={()=>deleteOffer(o.id)}>🗑</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── SETTINGS ── */}
      {tab === 'Settings' && (
        <div className="grid2">
          <div className="card">
            <div className="sec-title">Restaurant Info</div>
            {[['restaurantName','Restaurant Name','text','VIP Pizza'],['receiptTagline','Receipt Tagline','text','The Taste of Excellence'],['address','Address','text','Your full address'],['phone','Phone Number','text','+91 74648 48888'],['email','Email','email','info@vippizza.in'],['feedbackPhone','Feedback Phone','text','+91 74648 48888']].map(([k,l,t,p]) => (
              <div className="field" key={k}><label>{l}</label><input className="input" type={t} placeholder={p} value={settingsForm[k]||''} onChange={e=>setSettingsForm(f=>({...f,[k]:e.target.value}))} /></div>
            ))}
          </div>
          <div className="card">
            <div className="sec-title">Tax & Compliance</div>
            {[['gstNumber','GSTIN Number','text','09XXXXX1234Z1ZX'],['fssaiNumber','FSSAI Number','text','12345678901234']].map(([k,l,t,p]) => (
              <div className="field" key={k}><label>{l}</label><input className="input" type={t} placeholder={p} value={settingsForm[k]||''} onChange={e=>setSettingsForm(f=>({...f,[k]:e.target.value}))} /></div>
            ))}
            <div className="sec-title" style={{ marginTop: 16 }}>Receipt Messages</div>
            <div className="field"><label>Receipt Footer Message</label><input className="input" placeholder="Thank you for dining with us!" value={settingsForm.receiptFooter||''} onChange={e=>setSettingsForm(f=>({...f,receiptFooter:e.target.value}))} /></div>
            <div className="sec-title" style={{ marginTop: 16 }}>Loyalty Points</div>
            <div className="field"><label>Points expire after (days)</label>
              <input className="input" type="number" min="0" value={settingsForm.loyaltyExpiryDays ?? 30} onChange={e=>setSettingsForm(f=>({...f,loyaltyExpiryDays:e.target.value}))} />
              <div style={{ fontSize:11, color:'#aaa', marginTop:4 }}>Points not used within this many days of being earned expire (oldest points are used first). Enter 0 for no expiry. Points saved before expiry was switched on get a fresh period starting from the customer's next visit.</div>
            </div>
            <button className="btn btn-primary" style={{ width:'100%', marginTop:4 }} onClick={saveSettings} disabled={saving}>{saving?'Saving…':'💾 Save Settings'}</button>
          </div>
        </div>
      )}

      {/* ── CUSTOMER IMPORT PREVIEW ── */}
      {customerPreview && (
        <div className="modal-overlay">
          <div className="modal" style={{ width:680, maxWidth:'95vw', maxHeight:'88vh', overflowY:'auto' }}>
            <h3>⬆ Review customer import</h3>
            <div style={{ fontSize:12, color:'#888', marginBottom:12 }}>{customerPreview.fileName}</div>
            <div style={{ display:'flex', gap:8, flexWrap:'wrap', marginBottom:14 }}>
              {[
                ['Rows read', customerPreview.stats.rowsRead, '#555', '#f3f3f3'],
                ['New customers', customerPreview.creates.length, '#3B6D11', '#EAF3DE'],
                ['To update', customerPreview.updates.length, '#3B6D11', '#EAF3DE'],
                ['Unchanged', customerPreview.stats.unchanged, '#555', '#f3f3f3'],
                ['Skipped (errors)', customerPreview.errors.length, customerPreview.errors.length?'#b3261e':'#555', customerPreview.errors.length?'#fdecea':'#f3f3f3'],
              ].map(([label,val,color,bg])=>(
                <div key={label} style={{ background:bg, color, borderRadius:8, padding:'6px 12px', fontSize:12 }}>
                  <div style={{ fontSize:18, fontWeight:700 }}>{val}</div>{label}
                </div>
              ))}
            </div>
            {customerPreview.errors.length>0 && (
              <div style={{ background:'#fdecea', border:'1px solid #f5c2bd', borderRadius:8, padding:'8px 12px', marginBottom:12, fontSize:12, color:'#b3261e' }}>
                <div style={{ fontWeight:600, marginBottom:4 }}>These rows will be skipped:</div>
                {customerPreview.errors.slice(0,15).map((er,i)=><div key={i}>{er}</div>)}
                {customerPreview.errors.length>15 && <div>…and {customerPreview.errors.length-15} more</div>}
              </div>
            )}
            {(customerPreview.creates.length>0 || customerPreview.updates.length>0) ? (
              <table style={{ width:'100%', fontSize:12 }}>
                <thead><tr><th>Name</th><th>Phone</th><th>Address</th><th>What happens</th></tr></thead>
                <tbody>
                  {customerPreview.creates.slice(0,40).map(c=>(
                    <tr key={'n'+c.phone}><td style={{ fontWeight:500 }}>{c.name||'—'}</td><td>{c.phone}</td><td style={{ color:'#888' }}>{c.address||'—'}</td><td style={{ color:'#3B6D11' }}>New</td></tr>
                  ))}
                  {customerPreview.updates.slice(0,40).map(u=>(
                    <tr key={'u'+u.id}><td style={{ fontWeight:500 }}>{u.name||'—'}</td><td>{u.phone}</td><td style={{ color:'#888' }}>{u.data.address||'—'}</td><td style={{ color:'#B3702A' }}>Update {u.changes.join(', ')}</td></tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div style={{ textAlign:'center', color:'#999', padding:'18px 0' }}>Nothing new to import from this file.</div>
            )}
            {(customerPreview.creates.length>40 || customerPreview.updates.length>40) && <div style={{ fontSize:11, color:'#aaa', marginTop:6 }}>Showing the first 40 of each — everything will be imported.</div>}
            <div style={{ display:'flex', gap:8, justifyContent:'flex-end', marginTop:16 }}>
              <button className="btn" onClick={()=>setCustomerPreview(null)} disabled={custImporting}>Cancel</button>
              <button className="btn btn-primary" onClick={applyCustomerImport} disabled={custImporting || (!customerPreview.creates.length && !customerPreview.updates.length)}>
                {custImporting ? 'Importing…' : `Import ${customerPreview.creates.length} new${customerPreview.updates.length?` + update ${customerPreview.updates.length}`:''}`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── DELETE CATEGORY ── */}
      {deletingCat && (
        <div className="modal-overlay">
          <div className="modal" style={{ width:420, maxWidth:'95vw' }}>
            <h3>🗑 Delete “{deletingCat.name}”</h3>
            {itemsInCat(deletingCat.name).length>0 ? (
              <>
                <div style={{ fontSize:13, color:'#666', marginBottom:12 }}>
                  This category has <b>{itemsInCat(deletingCat.name).length}</b> item{itemsInCat(deletingCat.name).length>1?'s':''}. Choose where to move them — no items are deleted.
                </div>
                <div className="field"><label>Move items to</label>
                  <select className="input" value={deletingCat.moveTo} onChange={e=>setDeletingCat(d=>({...d,moveTo:e.target.value}))}>
                    <option value="">— choose a category —</option>
                    {categoryList.filter(c=>!sameCat(c,deletingCat.name)).map(c=><option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
              </>
            ) : (
              <div style={{ fontSize:13, color:'#666', marginBottom:12 }}>This category has no items. It will simply be removed from the list.</div>
            )}
            <div style={{ display:'flex', gap:8, justifyContent:'flex-end', marginTop:14 }}>
              <button className="btn" onClick={()=>setDeletingCat(null)} disabled={catBusy}>Cancel</button>
              <button className="btn btn-danger" onClick={deleteCategory} disabled={catBusy}>{catBusy?'Working…':(itemsInCat(deletingCat.name).length>0?'Move items & delete':'Delete')}</button>
            </div>
          </div>
        </div>
      )}

      {/* ── EXCEL UPLOAD PREVIEW ── */}
      {importPreview && (
        <div className="modal-overlay">
          <div className="modal" style={{ width:680, maxWidth:'95vw', maxHeight:'88vh', overflowY:'auto' }}>
            <h3>⬆ Review price changes</h3>
            <div style={{ fontSize:12, color:'#888', marginBottom:12 }}>{importPreview.fileName}</div>
            <div style={{ display:'flex', gap:8, flexWrap:'wrap', marginBottom:14 }}>
              {[
                ['Rows read', importPreview.stats.rowsRead, '#555', '#f3f3f3'],
                ['Price changes', importPreview.changes.length, '#3B6D11', '#EAF3DE'],
                ['Items affected', importPreview.stats.itemsAffected, '#3B6D11', '#EAF3DE'],
                ['Unchanged', importPreview.stats.unchanged, '#555', '#f3f3f3'],
                ['Errors', importPreview.errors.length, importPreview.errors.length?'#b3261e':'#555', importPreview.errors.length?'#fdecea':'#f3f3f3'],
              ].map(([label,val,color,bg])=>(
                <div key={label} style={{ background:bg, color, borderRadius:8, padding:'6px 12px', fontSize:12 }}>
                  <div style={{ fontSize:18, fontWeight:700 }}>{val}</div>{label}
                </div>
              ))}
            </div>

            {importPreview.errors.length>0 && (
              <div style={{ background:'#fdecea', border:'1px solid #f5c2bd', borderRadius:8, padding:'8px 12px', marginBottom:12, fontSize:12, color:'#b3261e' }}>
                <div style={{ fontWeight:600, marginBottom:4 }}>These rows will be skipped:</div>
                {importPreview.errors.slice(0,15).map((er,i)=><div key={i}>{er}</div>)}
                {importPreview.errors.length>15 && <div>…and {importPreview.errors.length-15} more</div>}
              </div>
            )}

            {importPreview.changes.length>0 ? (
              <>
                <table style={{ width:'100%', fontSize:12 }}>
                  <thead><tr><th>Item</th><th>Category</th><th>Size</th><th style={{ textAlign:'right' }}>Old</th><th style={{ textAlign:'right' }}>New</th></tr></thead>
                  <tbody>
                    {importPreview.changes.map((c,i)=>(
                      <tr key={i}>
                        <td style={{ fontWeight:500 }}>{c.name}</td>
                        <td style={{ color:'#888' }}>{c.cat}</td>
                        <td>{c.size}</td>
                        <td style={{ textAlign:'right', color:'#888' }}>₹{c.oldVal}</td>
                        <td style={{ textAlign:'right', fontWeight:600, color:'#D85A30' }}>{typeof c.newVal==='number'?`₹${c.newVal}`:'Auto'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div style={{ fontSize:11, color:'#aaa', marginTop:8 }}>Sizes without a custom price follow the base price automatically.</div>
              </>
            ) : (
              <div style={{ textAlign:'center', color:'#999', padding:'18px 0' }}>No price changes found in this file.</div>
            )}

            <div style={{ display:'flex', gap:8, justifyContent:'flex-end', marginTop:16 }}>
              <button className="btn" onClick={()=>setImportPreview(null)} disabled={importing}>Cancel</button>
              <button className="btn btn-primary" onClick={applyImport} disabled={importing || !importPreview.changes.length}>
                {importing ? 'Updating…' : `Apply ${importPreview.changes.length} change${importPreview.changes.length===1?'':'s'}`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── ITEM MODAL ── */}
      {itemModal && (
        <div className="modal-overlay">
          <div className="modal" style={{ width:480 }}>
            <h3>{itemModal==='add'?'+ Add Menu Item':`✏ Edit: ${itemModal.name}`}</h3>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
              <F label="Item Name"><input className="input" placeholder="e.g. Margherita" value={itemForm.name} onChange={e=>setItemForm(f=>({...f,name:e.target.value}))} /></F>
              <F label="Emoji"><input className="input" placeholder="🍕" value={itemForm.emoji} onChange={e=>setItemForm(f=>({...f,emoji:e.target.value}))} /></F>
              <F label="Category">
                <input className="input" placeholder="e.g. Bakery" value={itemForm.cat} onChange={e=>setItemForm(f=>({...f,cat:e.target.value}))} list="cat-list" />
                <datalist id="cat-list">{categoryList.map(c=><option key={c} value={c}/>)}</datalist>
              </F>
              <F label="Size type"><select className="input" value={itemForm.sizes} onChange={e=>setItemForm(f=>({...f,sizes:e.target.value}))}>{SIZES_OPTIONS.map(s=><option key={s} value={s}>{s}</option>)}</select></F>
              <F label="Has add-ons?"><select className="input" value={itemForm.hasAddon?'yes':'no'} onChange={e=>setItemForm(f=>({...f,hasAddon:e.target.value==='yes'}))}><option value="yes">Yes</option><option value="no">No</option></select></F>
            </div>
            <div style={{ marginTop:10 }}>
              <label style={{ fontSize:12, fontWeight:600, color:'#888', textTransform:'uppercase', display:'block', marginBottom:6 }}>Prices by size (₹)</label>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
                {(SIZES[itemForm.sizes] || SIZES.single).map(s => {
                  const isBase = s.mult === 1; // the first/base size — stored directly as basePrice
                  const computed = itemForm.basePrice ? autoSizePrice(itemForm.basePrice, s.mult) : '';
                  const override = itemForm.sizePrices?.[s.label];
                  const effective = isBase ? itemForm.basePrice : (override !== undefined && override !== '' ? override : computed);
                  return (
                    <F key={s.label} label={s.label}>
                      <input className="input" type="number"
                        placeholder={isBase ? '149' : (computed !== '' ? String(computed) : '')}
                        value={isBase ? itemForm.basePrice : (override ?? '')}
                        onChange={e => isBase
                          ? setItemForm(f=>({...f, basePrice:e.target.value}))
                          : setItemForm(f=>({...f, sizePrices:{...f.sizePrices, [s.label]:e.target.value}}))
                        } />
                      <div style={{ fontSize:10, color:'#aaa', marginTop:2 }}>
                        {effective !== '' ? `Will charge ₹${effective}` : 'Enter a price'}{!isBase && (override===undefined||override==='') ? ' (auto)' : ''}
                      </div>
                    </F>
                  );
                })}
              </div>
              {(SIZES[itemForm.sizes] || SIZES.single).length > 1 && (
                <div style={{ fontSize:11, color:'#aaa', marginTop:4 }}>{(SIZES[itemForm.sizes]||SIZES.single)[0].label} sets the base price. Leave any other size blank to auto-calculate it from the base price instead.</div>
              )}
            </div>
            <F label="Available?"><select className="input" value={itemForm.available!==false?'yes':'no'} onChange={e=>setItemForm(f=>({...f,available:e.target.value==='yes'}))}><option value="yes">✅ Visible on menu</option><option value="no">❌ Hidden from menu</option></select></F>
            <div style={{ display:'flex', gap:8, marginTop:12 }}>
              <button className="btn" style={{ flex:1 }} onClick={()=>setItemModal(null)}>Cancel</button>
              <button className="btn btn-primary" style={{ flex:1 }} onClick={saveItem} disabled={saving}>{saving?'Saving…':'Save Item'}</button>
            </div>
          </div>
        </div>
      )}

      {/* ── NEW ADDON GROUP MODAL ── */}
      {addonModal==='newGroup' && (
        <div className="modal-overlay">
          <div className="modal">
            <h3>+ New Addon Group</h3>
            <F label="Group Name"><input className="input" placeholder="e.g. Choose Your Sauce" value={addonGroupForm.name} onChange={e=>setAddonGroupForm(f=>({...f,name:e.target.value}))} /></F>
            <F label="Selection Type">
              <select className="input" value={addonGroupForm.selectionType} onChange={e=>setAddonGroupForm(f=>({...f,selectionType:e.target.value}))}>
                <option value="single">Single — customer picks ONE option</option>
                <option value="multiple">Multiple — customer picks MANY options</option>
              </select>
            </F>
            <F label="Applies to (item types)">
              <div style={{ display:'flex', gap:8, flexWrap:'wrap', marginTop:4 }}>
                {['pizza','pasta','burger','sandwich','all'].map(type=>(
                  <label key={type} style={{ display:'flex', alignItems:'center', gap:4, fontSize:13, cursor:'pointer' }}>
                    <input type="checkbox" style={{ accentColor:'#D85A30' }} checked={(addonGroupForm.appliesTo||[]).includes(type)}
                      onChange={e=>{ const arr = addonGroupForm.appliesTo||[]; setAddonGroupForm(f=>({...f, appliesTo: e.target.checked ? [...arr,type] : arr.filter(x=>x!==type)})); }} />
                    {type}
                  </label>
                ))}
              </div>
            </F>
            <div style={{ display:'flex', gap:8, marginTop:12 }}>
              <button className="btn" style={{ flex:1 }} onClick={()=>setAddonModal(null)}>Cancel</button>
              <button className="btn btn-primary" style={{ flex:1 }} onClick={addNewGroup} disabled={saving}>{saving?'Saving…':'Create Group'}</button>
            </div>
          </div>
        </div>
      )}

      {/* ── NEW ADDON OPTION MODAL ── */}
      {addonModal==='newOption' && (
        <div className="modal-overlay">
          <div className="modal">
            <h3>+ Add Option to {addonGroups.find(g=>g.id===editingGroup)?.name}</h3>
            <F label="Option Name"><input className="input" placeholder="e.g. Thin Crust" ref={addonNameRef} /></F>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:10 }}>
              <F label="Personal (₹)"><input className="input" type="number" defaultValue="0" ref={addonPersonalRef} /></F>
              <F label="Share (₹)"><input className="input" type="number" defaultValue="0" ref={addonShareRef} /></F>
              <F label="Family (₹)"><input className="input" type="number" defaultValue="0" ref={addonFamilyRef} /></F>
            </div>
            <div style={{ display:'flex', gap:8, marginTop:12 }}>
              <button className="btn" style={{ flex:1 }} onClick={()=>setAddonModal(null)}>Cancel</button>
              <button className="btn btn-primary" style={{ flex:1 }} onClick={()=>addOptionToGroup(editingGroup)}>Add Option</button>
            </div>
          </div>
        </div>
      )}

      {/* ── WEEKLY OFFER MODAL ── */}
      {weeklyModal && (
        <div className="modal-overlay">
          <div className="modal" style={{ width:500 }}>
            <h3>{weeklyModal==='add'?'+ Add Weekly Offer':'✏ Edit Weekly Offer'}</h3>
            <F label="Offer Name"><input className="input" placeholder="e.g. WOW Tuesday" value={weeklyForm.name} onChange={e=>setWeeklyForm(f=>({...f,name:e.target.value}))} /></F>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
              <F label="Day of Week"><select className="input" value={weeklyForm.dayOfWeek} onChange={e=>setWeeklyForm(f=>({...f,dayOfWeek:+e.target.value}))}>{DAYS.map((d,i)=><option key={i} value={i}>{d}</option>)}</select></F>
              <F label="Offer Type"><select className="input" value={weeklyForm.type} onChange={e=>setWeeklyForm(f=>({...f,type:e.target.value}))}><option value="fixed_price">Fixed Price (e.g. ₹469 per pizza)</option><option value="buy1get1">Buy 1 Get 1 Free</option></select></F>
            </div>
            <F label="Description (shown on billing screen)"><input className="input" placeholder="e.g. Any Family pizza at ₹469!" value={weeklyForm.description} onChange={e=>setWeeklyForm(f=>({...f,description:e.target.value}))} /></F>

            {weeklyForm.type === 'fixed_price' ? (
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
                <F label="Size that qualifies"><select className="input" value={weeklyForm.sizeFilter} onChange={e=>setWeeklyForm(f=>({...f,sizeFilter:e.target.value}))}>{PIZZA_SIZE_LABELS.map(s=><option key={s} value={s}>{s}</option>)}</select></F>
                <F label="Fixed Price (₹)"><input className="input" type="number" placeholder="469" value={weeklyForm.fixedPrice} onChange={e=>setWeeklyForm(f=>({...f,fixedPrice:e.target.value}))} /></F>
              </div>
            ) : (
              <div>
                <div style={{ fontSize:12, fontWeight:600, color:'#888', marginBottom:7 }}>Free pizza rules (size + minimum spend to qualify)</div>
                {weeklyForm.rules.map((r,idx)=>(
                  <div key={idx} style={{ display:'flex', gap:8, marginBottom:8, alignItems:'center' }}>
                    <select className="input" style={{ flex:1 }} value={r.size} onChange={e=>updateRuleRow(idx,'size',e.target.value)}>{PIZZA_SIZE_LABELS.map(s=><option key={s} value={s}>{s}</option>)}</select>
                    <span style={{ fontSize:12, color:'#888' }}>min ₹</span>
                    <input className="input" type="number" style={{ flex:1 }} placeholder="439" value={r.minPrice} onChange={e=>updateRuleRow(idx,'minPrice',e.target.value)} />
                    <button className="btn" style={{ padding:'4px 8px' }} onClick={()=>removeRuleRow(idx)}>✕</button>
                  </div>
                ))}
                <button className="btn" style={{ fontSize:12, marginBottom:8 }} onClick={addRuleRow}>+ Add size rule</button>
              </div>
            )}

            <F label="Status"><select className="input" value={weeklyForm.active?'yes':'no'} onChange={e=>setWeeklyForm(f=>({...f,active:e.target.value==='yes'}))}><option value="yes">✅ Active</option><option value="no">⏸ Inactive</option></select></F>

            <div style={{ display:'flex', gap:8, marginTop:12 }}>
              <button className="btn" style={{ flex:1 }} onClick={()=>setWeeklyModal(null)}>Cancel</button>
              <button className="btn btn-primary" style={{ flex:1 }} onClick={saveWeeklyOffer} disabled={saving}>{saving?'Saving…':'Save Offer'}</button>
            </div>
          </div>
        </div>
      )}

      {/* ── COMBO MODAL ── */}
      {comboModal && (
        <div className="modal-overlay">
          <div className="modal" style={{ width:560, maxHeight:'85vh', overflowY:'auto' }}>
            <h3>{comboModal==='add'?'+ Add Combo':'✏ Edit Combo'}</h3>
            <div style={{ display:'grid', gridTemplateColumns:'2fr 1fr', gap:10 }}>
              <F label="Combo Name"><input className="input" placeholder="e.g. Combo A" value={comboForm.name} onChange={e=>setComboForm(f=>({...f,name:e.target.value}))} /></F>
              <F label="Fixed Price (₹)"><input className="input" type="number" placeholder="399" value={comboForm.price} onChange={e=>setComboForm(f=>({...f,price:e.target.value}))} /></F>
            </div>
            <F label="Status"><select className="input" value={comboForm.active?'yes':'no'} onChange={e=>setComboForm(f=>({...f,active:e.target.value==='yes'}))}><option value="yes">✅ Active</option><option value="no">⏸ Inactive</option></select></F>

            <div style={{ fontSize:12, fontWeight:600, color:'#888', margin:'14px 0 8px' }}>Combo Slots</div>
            {comboForm.slots.map((slot, idx) => {
              const slotHasPizza = slot.itemIds.some(id => items.find(i=>i.id===id)?.sizes === 'pizza');
              return (
              <div key={slot.id} style={{ border:'1px solid #eee', borderRadius:9, padding:10, marginBottom:10 }}>
                <div style={{ display:'flex', gap:8, alignItems:'center', marginBottom:8 }}>
                  <input className="input" placeholder="Slot label e.g. Choose your Pizza" value={slot.label} onChange={e=>updateSlotLabel(idx, e.target.value)} style={{ flex:1 }} />
                  <button className="btn" style={{ padding:'5px 9px' }} onClick={()=>removeSlot(idx)}>🗑</button>
                </div>
                <div style={{ fontSize:11, color:'#888', marginBottom:6 }}>Select which items belong in this slot:</div>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:5, maxHeight:150, overflowY:'auto' }}>
                  {items.filter(i=>i.available!==false).map(item => (
                    <label key={item.id} style={{ display:'flex', alignItems:'center', gap:5, fontSize:11, cursor:'pointer' }}>
                      <input type="checkbox" checked={slot.itemIds.includes(item.id)} onChange={()=>toggleSlotItem(idx, item.id)} style={{ accentColor:'#D85A30' }} />
                      {item.emoji} {item.name}
                    </label>
                  ))}
                </div>
                {slotHasPizza && (
                  <div style={{ marginTop:9, padding:9, background:'#FAECE7', borderRadius:7 }}>
                    <div style={{ fontSize:11, fontWeight:600, color:'#993C1D', marginBottom:6 }}>
                      🍕 This slot has pizza — restrict which sizes customers can choose (leave all unchecked = allow every size)
                    </div>
                    <div style={{ display:'flex', flexWrap:'wrap', gap:6 }}>
                      {PIZZA_SIZE_LABELS.map(sizeLabel => {
                        const active = (slot.allowedSizes||[]).includes(sizeLabel);
                        return (
                          <div key={sizeLabel} onClick={()=>toggleSlotSize(idx, sizeLabel)} style={{
                            padding:'4px 10px', borderRadius:20, fontSize:11, cursor:'pointer',
                            border:`1px solid ${active?'#D85A30':'#ddd'}`,
                            background: active?'#D85A30':'#fff', color: active?'#fff':'#555',
                          }}>{sizeLabel}</div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
              );
            })}
            <button className="btn" style={{ fontSize:12, marginBottom:12 }} onClick={addSlot}>+ Add Slot</button>

            <div style={{ display:'flex', gap:8 }}>
              <button className="btn" style={{ flex:1 }} onClick={()=>setComboModal(null)}>Cancel</button>
              <button className="btn btn-primary" style={{ flex:1 }} onClick={saveCombo} disabled={saving}>{saving?'Saving…':'Save Combo'}</button>
            </div>
          </div>
        </div>
      )}

      {/* ── OFFER MODAL ── */}
      {offerModal && offerModal!=='newGroup' && offerModal!=='newOption' && (
        <div className="modal-overlay">
          <div className="modal">
            <h3>{offerModal==='add'?'+ Add Offer':'✏ Edit Offer'}</h3>
            <F label="Title"><input className="input" placeholder="e.g. Weekend Special" value={offerForm.title} onChange={e=>setOfferForm(f=>({...f,title:e.target.value}))} /></F>
            <F label="Description"><input className="input" placeholder="e.g. 20% off on all pizzas" value={offerForm.description} onChange={e=>setOfferForm(f=>({...f,description:e.target.value}))} /></F>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
              <F label="Discount Type"><select className="input" value={offerForm.type} onChange={e=>setOfferForm(f=>({...f,type:e.target.value}))}><option value="percentage">Percentage (%)</option><option value="flat">Flat (₹)</option></select></F>
              <F label={offerForm.type==='percentage'?'Discount (%)':'Discount (₹)'}><input className="input" type="number" value={offerForm.value} onChange={e=>setOfferForm(f=>({...f,value:e.target.value}))} /></F>
              <F label="Min Order (₹)"><input className="input" type="number" placeholder="0 = no min" value={offerForm.minOrder} onChange={e=>setOfferForm(f=>({...f,minOrder:e.target.value}))} /></F>
              <F label="Coupon Code (optional)"><input className="input" placeholder="e.g. PIZZA20" value={offerForm.code} onChange={e=>setOfferForm(f=>({...f,code:e.target.value.toUpperCase()}))} /></F>
            </div>
            <F label="Status"><select className="input" value={offerForm.active?'yes':'no'} onChange={e=>setOfferForm(f=>({...f,active:e.target.value==='yes'}))}><option value="yes">✅ Active</option><option value="no">⏸ Inactive</option></select></F>
            <div style={{ display:'flex', gap:8, marginTop:12 }}>
              <button className="btn" style={{ flex:1 }} onClick={()=>setOfferModal(null)}>Cancel</button>
              <button className="btn btn-primary" style={{ flex:1 }} onClick={saveOffer} disabled={saving}>{saving?'Saving…':'Save Offer'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}